import './EventFeed.css';
import React, { useState, useRef, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import sessionData from './sessionData.json';

function getInitials(name) {
  return name.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();
}

// Compact agreement scale - fills card width via viewBox + width=100%
function ResultScale({ before, after }) {
  const VW    = 340;   // viewBox width (coordinate space)
  const padL  = 16;
  const padR  = 16;
  const W     = VW - padL - padR;  // 308px of usable track
  const trackY = 38;
  const VH    = 66;
  const rB    = 5;
  const rA    = 6.5;
  const lblY  = trackY - rA - 9;        // percentage labels above circles
  const axisY = trackY + rA + 11;       // axis endpoint labels below circles

  const xOf = (pct) => padL + (pct / 100) * W;
  const x50 = xOf(50);
  const xB  = xOf(before);
  const xA  = xOf(after);

  // Trim the segment to each dot's actual radius (rA) so it begins exactly at
  // the dot edge - never under the translucent "before" marker.
  const movingRight = xA >= xB;
  const segX1 = movingRight ? xB + rA : xA + rA;
  const segX2 = movingRight ? xA - rA : xB - rA;

  return (
    <svg
      viewBox={`0 0 ${VW} ${VH}`}
      width="100%"
      style={{ display: 'block', overflow: 'visible' }}
      aria-hidden
    >
      {/* Full-width track */}
      <line x1={padL} y1={trackY} x2={padL + W} y2={trackY}
        stroke="rgba(134,203,241,0.12)" strokeWidth="2.5" strokeLinecap="round" />
      {/* 50% tick */}
      <line x1={x50} y1={trackY - 4} x2={x50} y2={trackY + 4}
        stroke="rgba(134,203,241,0.22)" strokeWidth="1" />
      {/* Before label - above, muted */}
      <text x={xB} y={lblY} textAnchor="middle"
        fontSize="14" fontWeight="600" fill="rgba(134,203,241,0.50)"
        fontFamily="'DM Sans', sans-serif">{before}%</text>
      {/* After label - above, bold near-white */}
      <text x={xA} y={lblY} textAnchor="middle"
        fontSize="14" fontWeight="700" fill="#D3ECFC"
        fontFamily="'DM Sans', sans-serif">{after}%</text>
      {/* Amber segment - always drawn left-to-right, ends flush at dot edges */}
      <line x1={segX1} y1={trackY} x2={segX2} y2={trackY}
        stroke="#D4A244" strokeWidth="2.5" strokeLinecap="butt" />
      {/* Before marker (hollow) - drawn on top so it cleanly covers segment end */}
      <circle cx={xB} cy={trackY} r={rA}
        fill="rgba(134,203,241,0.5)" />
      {/* After marker (amber filled) - covers segment end */}
      <circle cx={xA} cy={trackY} r={rA} fill="#D4A244" />
      {/* Axis endpoint labels */}
      <text x={padL} y={axisY} textAnchor="start"
        fontSize="11" fill="rgba(134,203,241,0.28)"
        fontFamily="'DM Sans', sans-serif">Disagree</text>
      <text x={padL + W} y={axisY} textAnchor="end"
        fontSize="11" fill="rgba(134,203,241,0.28)"
        fontFamily="'DM Sans', sans-serif">Agree</text>
    </svg>
  );
}

// ── ResultPills: top-3 ranked horizontal bars, label + pct inside the bar ──
function ResultPills({ options }) {
  const top = options.slice(0, 3);
  const max = Math.max(...top.map(o => o.pct));
  return (
    <div className="ef-result-pills">
      {top.map((o, i) => {
        const hasMov = o.pct_before != null;
        const gained = hasMov && o.pct > o.pct_before;
        return (
          <div key={i} className={`ef-rp-row${i === 0 ? ' ef-rp-row--top' : ''}`}>
            <div className="ef-rp-fill" style={{ width: `${(o.pct / max) * 100}%` }} />
            <span className="ef-rp-label">{o.label}</span>
            <span className="ef-rp-pct">
              {hasMov ? (
                <>
                  <span className="ef-rp-pct-before">{o.pct_before}</span>
                  <svg
                    className={`ef-rp-tri${gained ? ' ef-rp-tri--up' : ' ef-rp-tri--down'}`}
                    width="7" height="7" viewBox="0 0 7 7" aria-hidden="true"
                  >
                    <path d={gained ? 'M3.5 0L7 6H0z' : 'M3.5 7L0 1h7z'} fill="currentColor" />
                  </svg>
                  <span className="ef-rp-pct-after">{o.pct}%</span>
                </>
              ) : (
                <span className="ef-rp-pct-after">{o.pct}%</span>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── ResultCardContent ──
function ResultCardContent({ question }) {
  return (
    <>
      <div className="ef-well-head">
        <span className="ef-well-eyebrow">HOW THE ROOM MOVED</span>
        <span className="ef-well-scope">among participants</span>
      </div>
      <p className="ef-well-question">{question.text}</p>
      <div className="ef-well-viz">
        {question.type === 'agree_disagree' ? (
          <ResultScale before={question.result.before} after={question.result.after} />
        ) : (
          <ResultPills options={question.result.options} />
        )}
      </div>
    </>
  );
}

// ── BaselineCardContent ──
function BaselineCardContent({ question }) {
  const userAns = question.baseline.userAnswer;
  return (
    <>
      <div className="ef-well-head">
        <span className="ef-well-eyebrow">BEFORE THE TOWNHALL</span>
        <span className="ef-well-scope">your answer</span>
      </div>
      <p className="ef-well-question">{question.text}</p>
      <div className="ef-well-viz">
        {question.type === 'agree_disagree' ? (
          <div className="ef-bk-vote">
            <span className={`ef-bk-vbtn ef-bk-vbtn--agree${userAns === 'agree' ? ' on' : ''}`}>
              <svg viewBox="0 0 16 16"><path d="M8 3l5.5 9H2.5z" /></svg>
              Agree
            </span>
            <span className={`ef-bk-vbtn ef-bk-vbtn--disagree${userAns === 'disagree' ? ' on' : ''}`}>
              <svg viewBox="0 0 16 16"><path d="M8 13L2.5 4h11z" /></svg>
              Disagree
            </span>
          </div>
        ) : (
          <div className="ef-bk-pick">
            <svg className="ef-bk-pick-check" width="13" height="13" viewBox="0 0 14 14" fill="none"
              stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2.5 7.5l3 3 6-7" />
            </svg>
            <span className="ef-bk-pick-label">{userAns}</span>
          </div>
        )}
      </div>
    </>
  );
}

// ── QuestionSlider ──
function QuestionSlider({ questions, mode }) {
  const [idx, setIdx] = useState(0);
  const viewportRef = useRef(null);
  const [vpW, setVpW] = useState(0);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  const PEEK = 24;
  const GAP  = 10;

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    setVpW(el.offsetWidth);
    const ro = new ResizeObserver(([entry]) => setVpW(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Active card sits flush at the content's left edge; only the next card
  // peeks on the right. This keeps the first card aligned with the feed text.
  const cardW  = vpW ? vpW - PEEK : 0;
  const offset = vpW ? -idx * (cardW + GAP) : 0;
  const total  = questions.length;

  const go = (n) => { if (n >= 0 && n < total) setIdx(n); };

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = Math.abs(e.changedTouches[0].clientY - touchStartY.current);
    if (Math.abs(dx) > dy + 10 && Math.abs(dx) > 36) {
      go(dx < 0 ? idx + 1 : idx - 1);
    }
    touchStartX.current = null;
  };

  if (!questions.length) return null;

  return (
    <div className="ef-qs-wrap">
      <div
        className="ef-qs-viewport"
        ref={viewportRef}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div
          className="ef-qs-track"
          style={{
            transform: `translateX(${offset}px)`,
            transition: vpW ? 'transform 420ms cubic-bezier(0.25, 1.04, 0.5, 1)' : 'none',
          }}
        >
          {questions.map((q, i) => (
            <div
              key={q.id}
              className={`ef-well ef-qs-card${i === idx ? ' ef-qs-card--active' : ''}`}
              style={cardW ? { width: cardW } : undefined}
              onClick={() => i !== idx && go(i)}
            >
              {mode === 'result'
                ? <ResultCardContent question={q} />
                : <BaselineCardContent question={q} />
              }
            </div>
          ))}
        </div>
      </div>
      {total > 1 && (
        <div className="ef-qs-dots">
          {questions.map((_, i) => (
            <button
              key={i}
              className={`ef-qs-dot${i === idx ? ' active' : ''}`}
              onClick={() => go(i)}
              aria-label={`Question ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function EventFeed() {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.slice(0, 2) || 'en';

  const loc = (obj, key) => {
    if (lang === 'de' && obj[`${key}_de`]) return obj[`${key}_de`];
    if (lang === 'fr' && obj[`${key}_fr`]) return obj[`${key}_fr`];
    return obj[key] ?? '';
  };

  const { id } = useParams();
  const session = sessionData.sessions.find((s) => s.id === id) ?? sessionData.sessions[0];
  const quotes     = [...session.quotes].reverse();
  const topRef     = useRef(null);

  const [quoteState, setQuoteState] = useState(() =>
    Object.fromEntries(
      session.quotes.map((q) => [
        q.id,
        {
          responsesOpen: false,
          tab: 'takes',
          userVote: null,
          composeOpen: false,
          composeType: 'take',
          composeText: '',
          localTakes: [],
          localQuestions: [],
        },
      ])
    )
  );

  const update = (quoteId, patch) =>
    setQuoteState((prev) => ({
      ...prev,
      [quoteId]: { ...prev[quoteId], ...patch },
    }));

  const handleTabClick = (quoteId, tab) => {
    const qs = quoteState[quoteId];
    if (qs.responsesOpen && qs.tab === tab) {
      update(quoteId, { responsesOpen: false });
    } else {
      update(quoteId, { responsesOpen: true, tab });
    }
  };

  const submitResponse = (quoteId) => {
    const qs = quoteState[quoteId];
    if (!qs.composeText.trim()) return;
    const newItem = { name: 'You', tags: [], time: 'just now', text: qs.composeText.trim() };
    setQuoteState((prev) => {
      const s = prev[quoteId];
      return {
        ...prev,
        [quoteId]: {
          ...s,
          composeText: '',
          composeOpen: false,
          localTakes:     s.composeType === 'take'     ? [...s.localTakes, newItem]     : s.localTakes,
          localQuestions: s.composeType === 'question' ? [...s.localQuestions, newItem] : s.localQuestions,
        },
      };
    });
  };

  const speakerMap   = Object.fromEntries(session.panelists.map((p) => [p.id, p]));

  const [expandedCtx, setExpandedCtx] = useState(null);
  const toggleCtx = (id) => setExpandedCtx((prev) => (prev === id ? null : id));

  const [speakersOpen, setSpeakersOpen] = useState(true);
  const [infoOpen, setInfoOpen] = useState(true);

  const questions = session.questions || [];

  return (
    <div className="ef-root">

      <button className="ef-back" onClick={() => history.push('/feed')}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10 3L5 8l5 5" />
        </svg>
        {t('ef.back')}
      </button>

      {/* ── Session hero ── */}
      <div className="ef-hero">
        <h1 className="ef-hero-title">{loc(session, 'title')}</h1>

        {/* Venue - single muted line, no rule */}
        {session.venue && (
          <p className="ef-venue-line">
            <span className="ef-venue-primary">{session.venue}</span>
            {session.venue_institution && (
              <span className="ef-venue-secondary"> · {session.venue_institution}</span>
            )}
          </p>
        )}

        {/* ── Speakers section ── */}
        <div className="ef-section">
          <button className="ef-section-header" onClick={() => setSpeakersOpen(p => !p)}>
            <span className="ef-section-label">{t('ef.speakers')}</span>
            <svg className={`ef-section-chevron${speakersOpen ? ' open' : ''}`}
              width="12" height="12" viewBox="0 0 12 12" fill="none"
              stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 4l4 4 4-4"/>
            </svg>
          </button>
          {speakersOpen && (
            <div className="ef-section-body">
              {session.panelists.map((p) => {
                const roleRaw   = loc(p, 'role').replace(/^(Moderation|Modération)\s*[·•]\s*/i, '');
                const roleLabel = p.roleShort || roleRaw;
                return (
                  <div key={p.id} className="ef-sp-row">
                    <img className="ef-sp-av" src={p.photo} alt={loc(p, 'name')} />
                    <span className="ef-sp-name-role">
                      <span className="ef-sp-name">{loc(p, 'name')}</span>
                      <span className="ef-sp-dot"> · </span>
                      <span className="ef-sp-role">{roleLabel}</span>
                    </span>
                    {p.moderator && <span className="ef-sp-mod">Moderation</span>}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Session info section ── */}
        <div className="ef-section">
          <button className="ef-section-header" onClick={() => setInfoOpen(p => !p)}>
            <span className="ef-section-label">{t('ef.sessionInfo')}</span>
            <svg className={`ef-section-chevron${infoOpen ? ' open' : ''}`}
              width="12" height="12" viewBox="0 0 12 12" fill="none"
              stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 4l4 4 4-4"/>
            </svg>
          </button>
          {infoOpen && (
            <div className="ef-section-body">
              {session.context.map((item) => {
                const isOpen = expandedCtx === item.id;
                return (
                  <div
                    key={item.id}
                    className={`ef-ctx-item${isOpen ? ' open' : ''}`}
                    onClick={() => toggleCtx(item.id)}
                  >
                    <div className="ef-ctx-row">
                      <span className={`ef-ctx-tag ef-ctx-tag--${item.type.toLowerCase()}`}>{item.type}</span>
                      <span className="ef-ctx-title">{loc(item, 'title')}</span>
                      <svg className="ef-ctx-chevron" width="12" height="12" viewBox="0 0 12 12" fill="none"
                        stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 4l4 4 4-4" />
                      </svg>
                    </div>
                    {isOpen && (
                      <div className="ef-ctx-body">
                        {item.speaker && <span className="ef-ctx-speaker">{item.speaker}</span>}
                        <p>{loc(item, 'body')}</p>
                        {item.url && (
                          <a
                            className="ef-ctx-link"
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {t('ef.openSource')}
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Feed label ── */}
      <div className="ef-feed-header">
        <span className="ef-feed-label">{t('ef.liveFeed')}</span>
      </div>

      {/* ── Feed ── */}
      <div className="ef-feed" ref={topRef}>

        {/* ── Result bookend - NEWEST / Latest ── */}
        <div className="ef-bookend ef-bookend--result">
          <div className="ef-bk-meta">
            <span className="ef-quote-time">{questions[0]?.result?.timestamp || '17:36'}</span>
            <span className="ef-latest-tag">· Latest</span>
          </div>
          <QuestionSlider questions={questions} mode="result" />
        </div>

        {/* ── Speaker quotes (no Latest tag - result is Latest now) ── */}
        {quotes.map((quote) => {
          const qs          = quoteState[quote.id];
          const speaker     = speakerMap[quote.speakerId];
          const allTakes    = [...quote.takes, ...qs.localTakes];
          const allQuestions = [...quote.questions, ...qs.localQuestions];
          const activeItems = qs.tab === 'takes' ? allTakes : allQuestions;

          return (
            <div key={quote.id} className="ef-quote">
              <div className="ef-quote-meta">
                <span className="ef-quote-time">{quote.timestamp}</span>
              </div>

              <p className="ef-quote-text">
                <span className="ef-q">&ldquo;</span>
                {loc(quote, 'text')}
                <span className="ef-q">&rdquo;</span>
              </p>

              <div className="ef-quote-attribution">
                {speaker?.photo && (
                  <img src={speaker.photo} alt="" className="ef-speaker-thumb" />
                )}
                <span>{loc(speaker, 'name')}&ensp;&middot;&ensp;{loc(speaker, 'roleShort') || loc(speaker, 'role')}</span>
              </div>

              <div className="ef-actions">
                <div className="ef-btn-row">
                  <div className="ef-vote-btns">
                    <button
                      className={`ef-action-btn ef-agree-btn${qs.userVote === 'agree' ? ' on' : ''}${qs.userVote === 'disagree' ? ' dim' : ''}`}
                      onClick={() => update(quote.id, { userVote: qs.userVote === 'agree' ? null : 'agree' })}
                    >
                      <svg viewBox="0 0 16 16"><path d="M8 3l5.5 9H2.5z" /></svg>
                      {t('ef.agree')}
                    </button>
                    <button
                      className={`ef-action-btn ef-disagree-btn${qs.userVote === 'disagree' ? ' on' : ''}${qs.userVote === 'agree' ? ' dim' : ''}`}
                      onClick={() => update(quote.id, { userVote: qs.userVote === 'disagree' ? null : 'disagree' })}
                    >
                      <svg viewBox="0 0 16 16"><path d="M8 13L2.5 4h11z" /></svg>
                      {t('ef.disagree')}
                    </button>
                  </div>

                  <div className="ef-btn-sep" />

                  <div className="ef-response-btns">
                    <button
                      className={`ef-action-btn${qs.responsesOpen && qs.tab === 'takes' ? ' active' : ''}`}
                      onClick={() => handleTabClick(quote.id, 'takes')}
                    >
                      {t('ef.takes')}
                      {allTakes.length > 0 && <span className="ef-action-count">{allTakes.length}</span>}
                    </button>
                    <button
                      className={`ef-action-btn${qs.responsesOpen && qs.tab === 'questions' ? ' active' : ''}`}
                      onClick={() => handleTabClick(quote.id, 'questions')}
                    >
                      {t('ef.questions')}
                      {allQuestions.length > 0 && <span className="ef-action-count">{allQuestions.length}</span>}
                    </button>
                  </div>
                </div>
              </div>

              {qs.responsesOpen && (
                <div className="ef-responses-panel">
                  <p className="ef-curation-caption">{t('ef.curationCaption')}</p>
                  {activeItems.length > 0 ? (
                    <div className="ef-responses">
                      {activeItems.map((item, i) => {
                        const itemTags = (lang === 'de' && item.tags_de) ? item.tags_de
                          : (lang === 'fr' && item.tags_fr) ? item.tags_fr
                          : item.tags;
                        return (
                          <div key={i} className="ef-response">
                            <div className="ef-response-av">{getInitials(item.name)}</div>
                            <div className="ef-response-body">
                              <div className="ef-response-meta">
                                <span className="ef-response-name">{item.name}</span>
                                {item.staged && (
                                  <span className="ef-stage-badge">● {t('ef.stageBadge')}</span>
                                )}
                                {(itemTags || []).map((tag, ti) => (
                                  <span key={ti} className="ef-response-tag">{tag}</span>
                                ))}
                                <span className="ef-response-time">{loc(item, 'time')}</span>
                              </div>
                              <p className="ef-response-text">{loc(item, 'text')}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    !qs.composeOpen && (
                      <p className="ef-empty">
                        {qs.tab === 'takes' ? t('ef.noTakes') : t('ef.noQuestions')}
                      </p>
                    )
                  )}

                  {qs.composeOpen ? (
                    <div className="ef-compose">
                      <div className="ef-compose-type-bar">
                        <button
                          className={`ef-compose-type-btn${qs.composeType === 'take' ? ' active' : ''}`}
                          onClick={() => update(quote.id, { composeType: 'take' })}
                        >{t('ef.take')}</button>
                        <button
                          className={`ef-compose-type-btn${qs.composeType === 'question' ? ' active' : ''}`}
                          onClick={() => update(quote.id, { composeType: 'question' })}
                        >{t('ef.question')}</button>
                      </div>
                      <textarea
                        className="ef-compose-input"
                        rows={3}
                        placeholder={qs.composeType === 'take' ? t('ef.takePlaceholder') : t('ef.questionPlaceholder')}
                        value={qs.composeText}
                        onChange={(e) => update(quote.id, { composeText: e.target.value })}
                        autoFocus
                      />
                      <div className="ef-compose-actions">
                        <button className="ef-compose-cancel" onClick={() => update(quote.id, { composeOpen: false, composeText: '' })}>{t('ef.cancel')}</button>
                        <button
                          className={`ef-compose-submit${!qs.composeText.trim() ? ' disabled' : ''}`}
                          onClick={() => submitResponse(quote.id)}
                          disabled={!qs.composeText.trim()}
                        >{t('ef.submit')}</button>
                      </div>
                    </div>
                  ) : (
                    <button
                      className="ef-add-btn"
                      onClick={() => update(quote.id, { composeOpen: true, composeType: qs.tab === 'takes' ? 'take' : 'question' })}
                    >
                      {qs.tab === 'takes' ? t('ef.addTake') : t('ef.addQuestion')}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* ── Baseline bookend - OLDEST / Start ── */}
        <div className="ef-bookend ef-bookend--baseline">
          <div className="ef-bk-meta">
            <span className="ef-quote-time">{questions[0]?.baseline?.timestamp || '16:58'} · Start</span>
          </div>
          <QuestionSlider questions={questions} mode="baseline" />
        </div>

      </div>

      <div className="ef-copyright">Prototype by Ferdinand Riedl &middot; riedlsoftware.com</div>
    </div>
  );
}
